import {
    DID_YOU_DO_IT_BY_ERROR,
    MAIL_subject_WAS_MARKED_AS_SPAM,
    number_MESSAGES_WAS_DELETED,
} from 'mk/autogenerated/translations/sagas.baf8b864366b601de511e2fc3c5c0e25'
import { mail_api_delete_url, mail_api_report_url, mail_api_undelete_url } from 'mk/urls/functions';
import {
    MAIL_DELETE,
    MAIL_REPORT,
    MailDeleteAction,
    mailDeleteApi,
    mailDeleteCommit,
    mailDeleteOptimistic,
    mailDeleteUndo,
    MailReportAction,
    mailReportApi,
    mailUndeleteApi,
} from 'mk2/apps/mail/actions';
import { MailMinEntity } from 'mk2/apps/mail/schemas';
import {
    UNDO_TOAST_HIDDEN,
    UNDO_TOAST_UNDO,
    UndoToastHiddenAction,
    undoToastShow,
    UndoToastUndoAction,
} from 'mk2/containers/UndoToast/UndoToast.actions';
import { handleXHRPostErrorSaga, normalizeError, XHRAction, XHRPostError } from 'mk2/helpers/api';
import { getLogger } from 'mk2/logger';
import { getEntitiesEntity } from 'mk2/reducers';
import { interpolate } from 'mk2/services/i18n';
import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects';
import uuid from 'uuid/v4';

const logger = getLogger('mail/sagas');

function* mailDelete({ ids, xhr }: MailDeleteAction & XHRAction) {
    const undoId = uuid();

    // Optimistic remove from lists
    yield put(mailDeleteOptimistic(ids, undoId));

    yield put(mailDeleteApi.request(ids, undoId));

    try {
        const response = yield call(
            () => xhr.post(mail_api_delete_url(), { ids }),
        );
        // TODO: Add check response.body.ids vs ids
        yield put(mailDeleteApi.success(ids, undoId, response));

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);

        // TODO: What now? There was a failure - clear undo or revert or ignore?
        yield put(mailDeleteApi.failure(ids, undoId, normalizeError(error)));

        return;
    }

    const message = interpolate(number_MESSAGES_WAS_DELETED, { number: ids.length, count: ids.length });
    yield put(undoToastShow(undoId, `${message}. ${DID_YOU_DO_IT_BY_ERROR}`));

    const { undo, commit } = yield race({
        undo: take((action: UndoToastUndoAction) => action.type === UNDO_TOAST_UNDO && action.id === undoId),
        commit: take((action: UndoToastHiddenAction) => action.type === UNDO_TOAST_HIDDEN && action.id === undoId),
    });

    if (undo) {
        const preventLeaving = (event) => {
            const alert = 'Undeleting messages... do not leave or this action won\'t be finished and messages will be lost';
            (event || window.event).returnValue = alert;
            return alert;
        };

        // If the user leave page before the ajax is completed - messages will not be
        // undeleted even if he clicked on undo so we want to block the unload event
        window.addEventListener('beforeunload', preventLeaving);

        yield put(mailUndeleteApi.request(ids, undoId));

        try {
            const response = yield call(
                () => xhr.put(mail_api_undelete_url(), { ids }),
            );
            // TODO: Add check response.body.ids vs ids
            yield put(mailUndeleteApi.success(ids, undoId, response));

            yield put(mailDeleteUndo(undoId));

            window.removeEventListener('beforeunload', preventLeaving);
        } catch (error) {
            window.removeEventListener('beforeunload', preventLeaving);
            yield handleXHRPostErrorSaga(error as XHRPostError, logger);

            // TODO: What now? There was a failure - clear undo or revert or ignore?
            yield put(mailUndeleteApi.failure(ids, undoId, normalizeError(error)));

            return;
        }
    } else {
        yield put(mailDeleteCommit(undoId));
    }

}

function* mailReport({ id, xhr }: MailReportAction & XHRAction) {
    const undoId = uuid();

    // Optimistic remove from lists
    yield put(mailDeleteOptimistic([id], undoId));

    const mail: MailMinEntity = yield select(getEntitiesEntity, 'mails', id);

    const message = interpolate(MAIL_subject_WAS_MARKED_AS_SPAM, { subject: mail.subject });
    yield put(undoToastShow(undoId, `${message}. ${DID_YOU_DO_IT_BY_ERROR}`));

    const { undo, commit } = yield race({
        undo: take((action: UndoToastUndoAction) => action.type === UNDO_TOAST_UNDO && action.id === undoId),
        commit: take((action: UndoToastHiddenAction) => action.type === UNDO_TOAST_HIDDEN && action.id === undoId),
    });

    if (undo) {
        yield put(mailDeleteUndo(undoId));
    } else {

        yield put(mailReportApi.request(id));

        try {
            const response = yield call(
                () => xhr.post(mail_api_report_url(id)),
            );
            yield put(mailReportApi.success(id, undoId, response));

        } catch (error) {
            yield handleXHRPostErrorSaga(error as XHRPostError, logger);
            yield put(mailReportApi.failure(id, undoId, normalizeError(error)));
        }
    }
}

export default function* root() {
    yield all([
        takeEvery(MAIL_DELETE, mailDelete),
        takeEvery(MAIL_REPORT, mailReport),
    ]);
}
